// External libraries
import Grid from '@mui/material/Grid2';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

// API
import { useCreateFunctionMutation, useUpdateFunctionMutation } from '@api/functions/functions-api';

// Models
import { FunctionBaseApiModel } from '@models/ApiModels/function-base-api-model';
import { FunctionBase } from '@models/DomainModels/Master/function-base';

// Common components
import { CdfFormField } from '@commonComponents/forms/fields/CdfFormField';
import { EntityDetailDialog, EntityDetailDialogWrapper } from '@commonComponents/dialogs/EntityDetailDialog';
import { useEntityDetail, EntityDialogCloseResult } from '@commonComponents/dialogs/EntityDetailDialog/useEntityDetail';

// State management
import { DynamicStore } from '@state/interfaces/idynamic-store';

// Form Validation
import { FormFactory } from '@services/validation/form-factory';
import { useDecoratorForm } from '@hooks/useDecoratorForm';

// Permissions
import { useFetchToken } from '@hooks/useFetchToken';

// Props interface for the FunctionDetailDialog component
type FunctionDetailDialogProps = {
  functionData?: FunctionBase;
  organizationId: string;
  open: boolean;
  onClose: (result: EntityDialogCloseResult<FunctionBase>) => void;
  store: DynamicStore | undefined;
  token: string;
  readPermission?: boolean;
  writePermission?: boolean;
};

function FunctionDetailDialog({
  functionData,
  organizationId,
  open,
  onClose,
  token,
  readPermission = false,
  writePermission = false
}: Readonly<FunctionDetailDialogProps>) {
  const { t } = useTranslation(['functions', 'common']);

  // RTK Query hooks
  const [createFunction] = useCreateFunctionMutation();
  const [updateFunction] = useUpdateFunctionMutation();

  // Initialize form using FormFactory
  const formConfig = FormFactory.getFormConfig(
    FunctionBaseApiModel,
    functionData ?? {
      name: ''
    },
    { showSnackbarOnError: true }
  );

  const {
    data: currentFormData,
    errors: validationErrors,
    isValid,
    updateField,
    validateField,
    validateForm,
    resetForm,
    setData,
    setFormChanged,
    isDirty
  } = useDecoratorForm(formConfig.modelClass, formConfig.initialData, formConfig.options);

  // Memoize the isDirty value to prevent excessive re-renders
  const isDirtyValue = useMemo(() => isDirty(), [isDirty]);

  // Create an enhanced wrapper for validateForm with better logging and error handling
  const validateFormWrapper = useCallback(async (): Promise<boolean> => {
    // Run the decorator-based validation
    return (await validateForm()).isValid;
  }, [validateForm]);

  // Create entity function
  const createEntity = useCallback(async (): Promise<FunctionBase> => {
    const createdId = await createFunction({
      organizationId,
      functionBase: { name: currentFormData?.name ?? '' },
      token
    }).unwrap();

    return { ...currentFormData, id: createdId, organizationId } as FunctionBase;
  }, [createFunction, organizationId, token, currentFormData]);

  // Update entity function
  const updateEntity = useCallback(async (): Promise<FunctionBase> => {
    await updateFunction({
      organizationId,
      functionId: functionData?.id ?? '',
      functionBase: { name: currentFormData?.name ?? '' },
      token
    }).unwrap();

    return { ...currentFormData, id: functionData?.id ?? '', organizationId } as FunctionBase;
  }, [updateFunction, organizationId, functionData?.id, token, currentFormData]);

  // Create a wrapped onClose function that ensures the form is reset
  const handleDialogClose = useCallback(
    (result: EntityDialogCloseResult<FunctionBase>) => {
      resetForm();
      setFormChanged(false);
      onClose(result);
    },
    [onClose, resetForm, setFormChanged]
  );

  // Use the entity detail hook
  const { handleSave, loadingState } = useEntityDetail<FunctionBase>({
    entityName: t('entityName'),
    initialData: currentFormData as FunctionBase,
    open,
    onClose: handleDialogClose,
    createEntity,
    updateEntity,
    validateForm: validateFormWrapper,
    setData: data => setData(data as Partial<FunctionBaseApiModel>, true),
    resetForm,
    loggerContext: 'FunctionDetailDialog',
    readPermission,
    writePermission,
    setFormChanged
  });

  // Handle field changes
  const handleFieldChange = useCallback(
    (field: string, value: string | number) => {
      updateField(field, value);

      // Ensure we validate the field after updating
      setTimeout(() => validateField(field, value), 0);
    },
    [updateField, validateField]
  );

  // Handle field blur (validation)
  const handleFieldBlur = useCallback(
    (field: string) => {
      validateField(field, currentFormData?.[field as keyof FunctionBaseApiModel]);
    },
    [validateField, currentFormData]
  );

  // Create form content
  const formContent = (
    <Grid container size={12}>
      <Grid size={12}>
        <CdfFormField
          name="name"
          label={t('detailDialog.name')}
          value={currentFormData?.name ?? ''}
          required
          onChange={handleFieldChange}
          onBlur={handleFieldBlur}
          errors={validationErrors}
          data-testid="function-name-field"
          slotProps={{
            input: {
              'aria-label': t('detailDialog.name'),
              readOnly: !writePermission
            }
          }}
        />
      </Grid>
    </Grid>
  );

  // Get save button text based on edit vs. create mode
  const saveButtonText = functionData?.id ? t('saveChanges') : t('addButtonText');

  // Get cancel button text based on write permission
  const cancelButtonText = writePermission ? t('common:cancel') : t('common:close');

  // Get edit mode display title based on permissions
  const editModeTitle = writePermission ? t('edit') : t('view');

  // Get title text based on whether we're editing or creating
  const titleText = functionData?.id ? editModeTitle : t('add');

  return (
    <EntityDetailDialog
      open={open}
      onClose={() => handleDialogClose({ action: 'cancelled' })}
      title={titleText}
      formContent={formContent}
      saveButtonText={saveButtonText}
      cancelButtonText={cancelButtonText}
      readPermission={readPermission}
      writePermission={writePermission}
      isLoading={false}
      loadingState={loadingState}
      loadingMessage={t('detailDialog.loadingMessage')}
      savingMessage={t('detailDialog.savingMessage')}
      isValid={isValid}
      isDirty={isDirtyValue}
      onSave={handleSave}
      dataTestId="function-detail-dialog"
      maxWidth="xs"
    />
  );
}

// Props interface for the FunctionDetailDialogWrapper component
type FunctionDetailDialogWrapperProps = {
  functionData?: FunctionBase;
  organizationId: string;
  open: boolean;
  onClose: (result: EntityDialogCloseResult<FunctionBase>) => void;
  store: DynamicStore | undefined;
};

export function FunctionDetailDialogWrapper({ functionData, organizationId, open, onClose, store }: Readonly<FunctionDetailDialogWrapperProps>) {
  const token = useFetchToken();

  return (
    <EntityDetailDialogWrapper
      organizationId={organizationId}
      store={store}
      entityType="Functions"
      renderContent={permissions => (
        <FunctionDetailDialog
          functionData={functionData}
          organizationId={organizationId}
          open={open}
          onClose={onClose}
          store={store}
          token={token}
          readPermission={permissions.readPermission}
          writePermission={permissions.writePermission}
        />
      )}
    />
  );
}
